import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { KeyRoundIcon } from "lucide-react";
import { useState, type SubmitEvent } from "react";
import { z } from "zod";
import { AuthShell } from "@/components/auth-shell";
import { ErrorText } from "@/components/page";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { errorMessage } from "@/lib/api";
import { adoptAuthResult, loginWithPassword, session } from "@/lib/session";
import { loginWithPasskey } from "@/lib/webauthn";

const search = z.object({ redirect: z.string().optional(), reason: z.enum(["revoked", "expired"]).optional() });

export const Route = createFileRoute("/login")({
  validateSearch: search,
  beforeLoad: ({ search: s }) => {
    if (session.get().status !== "anon") throw redirect({ to: s.redirect ?? "/" });
  },
  component: LoginPage,
});

function LoginPage() {
  const nav = useNavigate();
  const { redirect: to, reason } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState<"password" | "passkey" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const done = () => void nav({ to: to ?? "/" });

  const onSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy("password");
    setError(null);
    try {
      await loginWithPassword(email, password);
      done();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const onPasskey = async () => {
    setBusy("passkey");
    setError(null);
    try {
      const { result, prfOutput, credentialId } = await loginWithPasskey();
      await adoptAuthResult(result, prfOutput, credentialId);
      done();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      description="Your password never leaves this device."
      footer={
        <>
          Forgot your password?{" "}
          <Link to="/recover" className="text-foreground underline underline-offset-4">
            Use your recovery key
          </Link>
        </>
      }
    >
      {reason === "revoked" ? (
        <Alert className="mb-4">
          <AlertDescription>This browser was revoked as a device. Sign in again to re-enroll it.</AlertDescription>
        </Alert>
      ) : null}
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username webauthn"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <ErrorText error={error} />
        <Button type="submit" className="w-full" disabled={busy !== null}>
          {busy === "password" ? "Unlocking…" : "Sign in"}
        </Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
        <Separator className="flex-1" /> or <Separator className="flex-1" />
      </div>
      <Button variant="outline" className="w-full" disabled={busy !== null} onClick={() => void onPasskey()}>
        <KeyRoundIcon /> {busy === "passkey" ? "Waiting for passkey…" : "Sign in with a passkey"}
      </Button>
    </AuthShell>
  );
}
